import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
} from '@angular/router';
import { filter } from 'rxjs';

// Per-route crawl control.
//
// The four `index.*.html` variants each set a sensible `<meta name="robots">`
// default for the whole app at build time. MBRS is internal-use only and not
// intended for public release, so every environment defaults to
// `noindex, nofollow`:
//   - src/index.html             (local / dev)      → noindex, nofollow
//   - src/index.staging.html     (staging VM)        → noindex, nofollow
//   - src/index.uat.html         (UAT VM)            → noindex, nofollow
//   - src/index.production.html  (production)        → noindex, nofollow
//
// Admin + developer routes still carry `data: { robots: 'noindex, nofollow' }`
// in src/app/app.routes.ts as defence-in-depth — today they restate the
// static default, but they keep the private surfaces private without a
// second edit if the production default is ever flipped to `index, follow`.
// This service is the mechanism that reads that route data on every
// navigation and writes the `<meta>` tag.
//
// Rules:
//  - On construction, snapshot whatever the HTML originally declared. That
//    captures the env-specific default baked into the selected index file
//    and becomes the fallback when a route does not specify its own value.
//  - On every NavigationEnd, walk the activated-route tree; the DEEPEST
//    child with a `robots` value wins. That means a nested public route
//    inside an admin section could opt back in, though we do not use that
//    today.
//  - If no route in the current tree specifies `data.robots`, RESTORE the
//    original HTML default. Today every environment's HTML default is
//    `noindex, nofollow`, so the restore is a no-op in practice — but the
//    behaviour matters the moment any environment relaxes its default to
//    `index, follow`. Without this restore step, a guard-redirect flow
//    (e.g. an unauthenticated user hitting `/mbrs/user-management`, having
//    their meta set to `noindex, nofollow` by the admin route's
//    `data.robots`, then being client-side-redirected to `/auth/login` by
//    `isAdminGuard`) would leave the login page silently stuck on the
//    admin route's noindex state and get dropped from the index.
//  - Non-executing crawlers only see the static HTML default; admin and
//    dev URLs are additionally gated by server-side auth redirects, so
//    they never see anything other than the login page anyway.
@Injectable({ providedIn: 'root' })
export class RobotsMetaService {
  private readonly router = inject(Router);
  private readonly meta = inject(Meta);
  private readonly destroyRef = inject(DestroyRef);

  // Snapshot once, on construction, BEFORE any NavigationEnd has fired.
  // Whatever value is in the HTML at this point is the env-specific
  // default chosen via angular.json's per-configuration `index` override.
  private readonly htmlDefault: string | null = this.readCurrentRobotsMeta();

  start(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.apply());
  }

  private apply(): void {
    const routeValue = this.deepestRobots(
      this.router.routerState.snapshot.root,
    );
    const target = routeValue ?? this.htmlDefault;
    if (target === null) {
      // HTML had no <meta name="robots"> and no route specifies one —
      // nothing to write. (Not expected in this app, but guards against
      // clobbering unrelated behaviour if the index files are changed.)
      return;
    }
    this.meta.updateTag({ name: 'robots', content: target });
  }

  private deepestRobots(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | undefined = route;
    let found: string | null = null;
    while (current) {
      const value = current.data?.['robots'];
      if (typeof value === 'string' && value.length > 0) {
        found = value;
      }
      current = current.firstChild ?? undefined;
    }
    return found;
  }

  private readCurrentRobotsMeta(): string | null {
    const tag = this.meta.getTag('name="robots"');
    const content = tag?.getAttribute('content');
    return content && content.length > 0 ? content : null;
  }
}
